import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import useDuplicateEmailCheck from "../Utilities/useDuplicateEmailCheck";
import {
  validateEmailField,
  validateUsernameField,
  validatePasswordField,
  validateConfirmPasswordField,
} from "../registrationHelpers";
import { getRegisterVariant, getInitialFormData } from "../registerPageConfig";
import {
  REGISTER_SUBMITTERS,
  handleRegisterAxiosError,
  handleRegisterApiClientError,
} from "../registerSubmitHandlers";

const noopDuplicate = {
  existingAccounts: [],
  hasDuplicate: false,
  isRegistrationBlocked: false,
  showPendingInviteWarning: false,
  checkEmail: () => {},
  reset: () => {},
  getAlertProps: () => ({ accounts: [], email: "", hasPendingInvite: false }),
  isSubmitDisabled: (loading, extraDisabled = false) => loading || extraDisabled,
  clearIfBlocked: () => {},
};

function clearIfSet(value, setter) {
  if (value) setter("");
}

function clearDuplicateIfNeeded(duplicateEmailCheck) {
  if (typeof duplicateEmailCheck.clearIfBlocked === "function") {
    duplicateEmailCheck.clearIfBlocked();
  } else if (duplicateEmailCheck.hasDuplicate) {
    duplicateEmailCheck.reset();
  }
}

/**
 * @param {import('../registerPageConfig').RegisterVariantId} variantId
 * @param {object} [options]
 * @param {string} [options.tokenFromRoute]
 * @param {boolean} [options.includePhoneField] pgContact: include phone in validation + submit
 */
export function useRegister(variantId, options = {}) {
  const { tokenFromRoute, includePhoneField } = options;
  const navigate = useNavigate();
  const params = useParams();
  const config = useMemo(() => getRegisterVariant(variantId), [variantId]);

  const routeToken = tokenFromRoute ?? params.token;
  const effectiveToken = config.useToken ? routeToken : undefined;

  const [formData, setFormData] = useState(() => getInitialFormData(variantId));
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [openCalculator, setOpenCalculator] = useState(false);
  const [openSupport, setOpenSupport] = useState(false);

  const [emailError, setEmailError] = useState("");
  const [emailAddressError, setEmailAddressError] = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [nameError, setNameError] = useState("");
  const [phonenumberError, setPhonenumberError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");

  const dupHook = useDuplicateEmailCheck(effectiveToken);
  const duplicateEmailCheck = config.useDuplicateEmailCheck ? dupHook : noopDuplicate;

  useEffect(() => {
    if (variantId === "pgContact" && tokenFromRoute) {
      setFormData((prev) => ({ ...prev, token: tokenFromRoute || "" }));
    }
  }, [variantId, tokenFromRoute]);

  const getEmailValue = useCallback(() => {
    if (config.emailField === "email_address") return formData.email_address;
    return formData.email;
  }, [config.emailField, formData]);

  const validateEmail = useCallback(() => {
    const v = getEmailValue();
    const result = validateEmailField(v || "");
    if (config.emailField === "email_address") {
      setEmailAddressError(result.error);
      setEmailError("");
    } else {
      setEmailError(result.error);
      setEmailAddressError("");
    }
    return result.isValid;
  }, [config.emailField, getEmailValue]);

  const validateEmailAddressField = useCallback(() => {
    const result = validateEmailField(formData.email_address || "");
    setEmailAddressError(result.error);
    return result.isValid;
  }, [formData.email_address]);

  const validateUsername = useCallback(() => {
    const result = validateUsernameField(formData.username);
    if (!result.isValid) {
      setUsernameError(result.error);
      return false;
    }
    const min = config.usernameMinLength;
    if (min != null && formData.username.length < min) {
      setUsernameError(`Username must be at least ${min} characters`);
      return false;
    }
    setUsernameError("");
    return true;
  }, [config.usernameMinLength, formData.username]);

  const validateName = useCallback(() => {
    if (!formData.name || !String(formData.name).trim()) {
      setNameError("Full name is required");
      return false;
    }
    setNameError("");
    return true;
  }, [formData.name]);

  const validatePhonenumber = useCallback(() => {
    if (!formData.phonenumber || !String(formData.phonenumber).trim()) {
      setPhonenumberError("Phone number is required");
      return false;
    }
    setPhonenumberError("");
    return true;
  }, [formData.phonenumber]);

  const validatePhonePg = useCallback(() => {
    if (!formData.phone_number || !String(formData.phone_number).trim()) {
      setPhoneError("Phone number is required");
      return false;
    }
    const phoneDigits = String(formData.phone_number).replace(/\D/g, "");
    if (phoneDigits.length < 10) {
      setPhoneError("Please enter a valid phone number");
      return false;
    }
    setPhoneError("");
    return true;
  }, [formData.phone_number]);

  const validatePassword = useCallback(() => {
    const result = validatePasswordField(formData.password);
    setPasswordError(result.error);
    return result.isValid;
  }, [formData.password]);

  const validateConfirmPassword = useCallback(() => {
    const result = validateConfirmPasswordField(
      formData.password,
      formData.confirmPassword
    );
    setConfirmPasswordError(result.error);
    return result.isValid;
  }, [formData.password, formData.confirmPassword]);

  const clearErrorForField = useCallback(
    (name) => {
      switch (name) {
        case "email_address":
          clearIfSet(emailAddressError, setEmailAddressError);
          clearDuplicateIfNeeded(duplicateEmailCheck);
          break;
        case "email":
          clearIfSet(emailError, setEmailError);
          clearDuplicateIfNeeded(duplicateEmailCheck);
          break;
        case "username":
          clearIfSet(usernameError, setUsernameError);
          break;
        case "name":
          clearIfSet(nameError, setNameError);
          break;
        case "phonenumber":
          clearIfSet(phonenumberError, setPhonenumberError);
          break;
        case "phone_number":
          clearIfSet(phoneError, setPhoneError);
          break;
        case "password":
          clearIfSet(passwordError, setPasswordError);
          clearIfSet(confirmPasswordError, setConfirmPasswordError);
          break;
        case "confirmPassword":
          clearIfSet(confirmPasswordError, setConfirmPasswordError);
          break;
        default:
          break;
      }
    },
    [
      confirmPasswordError,
      duplicateEmailCheck,
      emailAddressError,
      emailError,
      nameError,
      passwordError,
      phoneError,
      phonenumberError,
      usernameError,
    ]
  );

  const handleInputChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
      clearErrorForField(name);
    },
    [clearErrorForField]
  );

  const runValidationsForSubmit = useCallback(() => {
    const vals = [];

    switch (variantId) {
      case "professionalGuardian":
      case "professionalGuardianInvite":
        vals.push(
          validateEmailAddressField(),
          validateUsername(),
          validatePassword(),
          validateConfirmPassword()
        );
        break;
      case "lawFirmUser":
        vals.push(
          validateEmail(),
          validateUsername(),
          validatePassword(),
          validateConfirmPassword()
        );
        break;
      case "internalUser":
        vals.push(
          validateUsername(),
          validateName(),
          validatePassword(),
          validateConfirmPassword()
        );
        break;
      case "teamMember":
        vals.push(
          validateUsername(),
          validateEmail(),
          validateName(),
          validatePhonenumber(),
          validatePassword(),
          validateConfirmPassword()
        );
        break;
      case "pgContact":
        vals.push(
          validateUsername(),
          validateEmail(),
          validatePassword(),
          validateConfirmPassword()
        );
        if (includePhoneField) vals.push(validatePhonePg());
        break;
      default:
        break;
    }

    return vals.every(Boolean);
  }, [
    includePhoneField,
    validateConfirmPassword,
    validateEmail,
    validateEmailAddressField,
    validateName,
    validatePassword,
    validatePhonePg,
    validatePhonenumber,
    validateUsername,
    variantId,
  ]);

  const handleRegister = useCallback(
    async (event) => {
      if (event?.preventDefault && !config.skipPreventDefaultOnSubmit) {
        event.preventDefault();
      }

      if (!runValidationsForSubmit()) {
        if (config.validateAllToast) {
          toast.error(config.validateAllToast);
        }
        return;
      }

      if (config.useDuplicateEmailCheck && duplicateEmailCheck.isRegistrationBlocked) {
        if (duplicateEmailCheck.showPendingInviteWarning) {
          toast.error(
            "A pending invitation exists for this email. Use the link in your invite email to register."
          );
        } else {
          toast.error(
            "This email is already registered. Please sign in or recover your username."
          );
        }
        return;
      }

      setLoading(true);
      try {
        const submitFn = REGISTER_SUBMITTERS[variantId];
        if (!submitFn) return;

        await submitFn(
          {
            formData,
            token: effectiveToken,
            includePhone: variantId === "pgContact" ? !!includePhoneField : undefined,
          },
          { navigate, toast }
        );
      } catch (error) {
        if (config.httpClient === "apiClientRaw") {
          handleRegisterAxiosError(error, variantId);
        } else {
          handleRegisterApiClientError(error, variantId);
        }
      } finally {
        setLoading(false);
      }
    },
    [
      config.skipPreventDefaultOnSubmit,
      config.useDuplicateEmailCheck,
      config.validateAllToast,
      config.httpClient,
      duplicateEmailCheck.isRegistrationBlocked,
      duplicateEmailCheck.showPendingInviteWarning,
      effectiveToken,
      formData,
      includePhoneField,
      navigate,
      runValidationsForSubmit,
      variantId,
    ]
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter") handleRegister(event);
    },
    [handleRegister]
  );

  const onEmailBlurDupCheck = useCallback(() => {
    const ok =
      config.emailField === "email_address"
        ? validateEmailAddressField()
        : validateEmail();
    if (ok && config.useDuplicateEmailCheck) {
      duplicateEmailCheck.checkEmail(getEmailValue());
    }
  }, [
    config.emailField,
    config.useDuplicateEmailCheck,
    duplicateEmailCheck,
    getEmailValue,
    validateEmail,
    validateEmailAddressField,
  ]);

  return {
    config,
    variantId,
    formData,
    loading,
    showPassword,
    setShowPassword,
    showConfirmPassword,
    setShowConfirmPassword,
    openCalculator,
    setOpenCalculator,
    openSupport,
    setOpenSupport,
    handleInputChange,
    handleRegister,
    handleKeyDown,
    onEmailBlurDupCheck,
    duplicateEmailCheck,
    effectiveToken,
    errors: {
      email: emailError,
      email_address: emailAddressError,
      username: usernameError,
      name: nameError,
      phonenumber: phonenumberError,
      phone_number: phoneError,
      password: passwordError,
      confirmPassword: confirmPasswordError,
    },
    validateUsername,
    validateName,
    validatePhonenumber,
    validatePassword,
    validateConfirmPassword,
    validatePhonePg,
    validateEmailAddressField,
    validateEmail,
  };
}
